import { AccountButtonProps } from "@omni/types/components/props/accountButton.props";
import { BaseStore } from "@omni/types/enums/baseStore.enum";
import { PageLinkComponentProps } from "@omni/ui/components/PageLink/pageLink.props";
import { attempt } from "@omni/utils/function.utils";
import { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { AccountButtonContextProvider } from "@/components/Navigation/Buttons/AccountButton/context/accountButton.context";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import * as SoftLoginService from "@/services/softLogin.service";
import { rewriteAccountLinksForSoftLogin } from "@/utils/account";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";

type Props = AccountButtonProps<BaseStore> & {
    children: ReactNode;
};

export const Base = ({
    // HeaderButton props
    text,
    ariaLabel,
    hiddenText,
    icon,
    link,
    invert,
    invertMobile,
    showMenuOnHover,
    alerts,

    // AccountButton props
    menuLinksSettings,
    redirect,

    // Local props
    children
}: Props) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const customer = useSelector(CustomerSelectors.getCustomer);
    const isClientSide = useIsClientSide();
    const [parsedMenuLinks, setParsedMenuLinks] =
        useState<Array<PageLinkComponentProps>>();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isClientSide) {
            dispatch(CustomerModule.load());
        }
    }, [isClientSide]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const isLoggedIn = !!(customer && !customer.guest);

        if (menuLinksSettings) {
            setParsedMenuLinks(
                rewriteAccountLinksForSoftLogin(
                    isLoggedIn,
                    attempt(() => JSON.parse(menuLinksSettings), undefined),
                    link
                )
            );
        }
    }, [customer]);

    let firstName;
    let isCustomer = false;
    let userIsLoggedIn = false;

    if (isClientSide) {
        firstName = SoftLoginService.getSoftLoginDetails().firstName;
        isCustomer = !!(customer && !customer.guest);
        userIsLoggedIn = isCustomer || SoftLoginService.isSoftLoggedIn();

        if (isCustomer) {
            firstName = customer?.firstName;
        }
    }

    return (
        <AccountButtonContextProvider
            isLoggedIn={userIsLoggedIn}
            headerButtonProps={{
                text:
                    firstName && userIsLoggedIn
                        ? t("general.header.account.welcome", firstName)
                        : text,
                ariaLabel: ariaLabel,
                hiddenText: hiddenText,
                icon: icon,
                link: isCustomer ? redirect : link,
                invert: !!invert,
                invertMobile: !!invertMobile,
                showMenuOnHover: showMenuOnHover,
                alerts: alerts ? `${alerts}` : undefined
            }}
            links={userIsLoggedIn ? parsedMenuLinks : undefined}
        >
            {children}
        </AccountButtonContextProvider>
    );
};
